import {produce} from 'immer';

import * as FileUploadHelpers from 'web-app/react/components/file-upload/typed-helpers';
import type * as FileUploadTypes from 'web-app/react/components/file-upload/types';
import {type NonEmptyArray} from 'web-app/util/non-empty-array';
import {exhaustiveCheck, hasValue} from 'web-app/util/typescript';

import {
    getNextSelectedFileIndex,
    getNextSelectedFileIndexAfterDiscarding,
    getPersistedObjectId,
    getPreviousSelectedFileIndex,
    hasTaggedChildren,
} from './helpers';

export type ChildIds = NonEmptyArray<string>;

type TaggedChildren = 'undecided' | 'no-children' | ChildIds;

export interface TaggedFile {
    fileId: string;
    mediaType: FileUploadTypes.MediaType;
    taggedChildren: TaggedChildren;
    originalFile: FileUploadTypes.CustomFile;
    isPersisted: boolean;
}

export type TaggedFiles = TaggedFile[];

export interface State {
    taggedFiles: TaggedFiles;
    selectedTaggedFileIndex?: number;
    filter: string;
    didInitTaggedMedia?: string[];
    tracking: {
        startFlowTimestamp: Date;
        taggingFlowId: string;
    };
}

export type Action =
    | {
          type: 'AddFile';
          originalFile: FileUploadTypes.CustomFile;
      }
    | {
          type: 'PersistFile';
          persistedFile: FileUploadTypes.PersistedObject;
          originalFile: FileUploadTypes.CustomFile;
      }
    | {
          type: 'SelectNextFile';
      }
    | {
          type: 'SelectPreviousFile';
      }
    | {
          type: 'DiscardFile';
          fileId: string;
      }
    | {
          type: 'TagChild';
          childId: string;
      }
    | {
          type: 'UntagChild';
          childId: string;
      }
    | {
          type: 'ConfirmWithoutChildren';
      }
    | {
          type: 'ChangeFilter';
          filter: string;
      }
    | {
          type: 'InitTaggedChildren';
          fileId: string;
          taggedChildrenIds: ChildIds;
      };

export const reducer = produce((draft: State, action: Action) => {
    switch (action.type) {
        case 'AddFile' as const: {
            if (action.originalFile.id) {
                draft.taggedFiles = draft.taggedFiles.concat({
                    fileId: action.originalFile.id,
                    mediaType: FileUploadHelpers.getFileMediaType(action.originalFile),
                    taggedChildren: 'undecided',
                    originalFile: action.originalFile,
                    isPersisted: false,
                });
            }
            break;
        }
        case 'PersistFile' as const: {
            const fileIndex = draft.taggedFiles.findIndex(taggedFile => {
                return taggedFile.fileId === action.originalFile.id;
            });

            if (fileIndex !== -1) {
                const persistedObjectId = getPersistedObjectId(action.persistedFile);
                draft.taggedFiles[fileIndex].fileId = persistedObjectId;
                draft.taggedFiles[fileIndex].isPersisted = true;
                draft.selectedTaggedFileIndex = !hasValue(draft.selectedTaggedFileIndex)
                    ? 0
                    : draft.selectedTaggedFileIndex;
            }
            break;
        }
        case 'SelectNextFile' as const:
            draft.selectedTaggedFileIndex = getNextSelectedFileIndex(draft.selectedTaggedFileIndex, draft.taggedFiles);
            break;
        case 'SelectPreviousFile' as const:
            draft.selectedTaggedFileIndex = getPreviousSelectedFileIndex(
                draft.selectedTaggedFileIndex,
                draft.taggedFiles,
            );
            break;
        case 'DiscardFile' as const:
            draft.selectedTaggedFileIndex = getNextSelectedFileIndexAfterDiscarding(
                draft.selectedTaggedFileIndex,
                draft.taggedFiles,
            );
            draft.taggedFiles = draft.taggedFiles.filter(taggedFile => taggedFile.fileId !== action.fileId);
            break;
        case 'TagChild' as const: {
            if (hasValue(draft.selectedTaggedFileIndex)) {
                const taggedChildren = hasTaggedChildren(
                    draft.taggedFiles[draft.selectedTaggedFileIndex]?.taggedChildren,
                )
                    ? (draft.taggedFiles[draft.selectedTaggedFileIndex].taggedChildren.concat(
                          action.childId,
                      ) as TaggedChildren)
                    : ([action.childId] as TaggedChildren);

                draft.taggedFiles[draft.selectedTaggedFileIndex].taggedChildren = taggedChildren;
            }
            break;
        }
        case 'UntagChild' as const: {
            if (hasValue(draft.selectedTaggedFileIndex)) {
                const taggedChildren = draft.taggedFiles[draft.selectedTaggedFileIndex]?.taggedChildren;

                if (!hasTaggedChildren(taggedChildren)) {
                    break;
                }

                const updatedTaggedChildren = taggedChildren.filter(
                    childId => childId !== action.childId,
                ) as TaggedChildren;

                draft.taggedFiles[draft.selectedTaggedFileIndex].taggedChildren =
                    updatedTaggedChildren.length > 0 ? updatedTaggedChildren : 'undecided';
            }
            break;
        }
        case 'ConfirmWithoutChildren' as const: {
            if (hasValue(draft.selectedTaggedFileIndex)) {
                draft.taggedFiles[draft.selectedTaggedFileIndex].taggedChildren = 'no-children';
            }
            break;
        }
        case 'ChangeFilter' as const: {
            draft.filter = action.filter;
            break;
        }
        case 'InitTaggedChildren' as const: {
            draft.taggedFiles = draft.taggedFiles.map(file => {
                if (file.fileId !== action.fileId) {
                    return file;
                }

                return {...file, taggedChildren: action.taggedChildrenIds};
            });
            draft.didInitTaggedMedia = draft.didInitTaggedMedia
                ? [...draft.didInitTaggedMedia, action.fileId]
                : [action.fileId];
            break;
        }
        default:
            exhaustiveCheck(action);
    }
});
